import { Box, Typography, Modal, Select, MenuItem, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { getMarketingResource } from 'app/services/marketing';
import TextTutorialIcon from 'app/assets/icons/text-tutorial.svg?react';
import VideoTutorialIcon from 'app/assets/icons/video-tutorial.svg?react';
import proIcon from 'app/assets/pro.svg';
import useEventTracker from 'app/hooks/useEventTracker';
import usePaymentOverdue from 'app/hooks/usePaymentOverdue';
import { MANAGED_REPEATS_EVENTS } from 'app/tracking';

import PharmacySelector from './PharmacySelector';
import OrganisationSelector from './OrganisationSelector';
import HeaderDropdown from './common/HeaderDropdown';

function Header({ options }) {
  const {
    pharmacySelector = false,
    pharmacySelectorDisabled = false,
    pharmacySelectorHidden = false,
    organisationSelector = false,
    background: customBackground,
  } = options || {};

  const trackEvent = useEventTracker();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { headerActions, headerTitle, headerTitleDisabled } = useSelector(({ header }) => header);
  const { selectedPharmacyId } = useSelector((state) => state.pharmacySelector);
  const { isBlocked: isPaymentBlocked } = usePaymentOverdue();

  const [videoTutorialOpened, setVideoTutorialOpened] = useState(false);
  const [generatingTextTutorial, setGeneratingTextTutorial] = useState(false);

  // Check if we're on managed repeats page
  const isOnManagedRepeatsPage = location.pathname.startsWith('/managed-repeats');

  const videoTutorial = useMemo(() => {
    return headerActions.find((action) => action.type === 'video_tutorial');
  }, [headerActions]);
  const textTutorial = useMemo(() => {
    return headerActions.find((action) => action.type === 'text_tutorial');
  }, [headerActions]);

  async function handleTextTutorialClick() {
    if (generatingTextTutorial) return;

    // Track view guide click if on managed repeats page
    if (isOnManagedRepeatsPage) {
      trackEvent(
        MANAGED_REPEATS_EVENTS.VIEW_GUIDE_CLICKED.action,
        MANAGED_REPEATS_EVENTS.VIEW_GUIDE_CLICKED
      );
    }

    try {
      setGeneratingTextTutorial(true);
      const { url } = await getMarketingResource(selectedPharmacyId, textTutorial?.resource, false);
      window.open(url, '_blank', 'noreferrer');
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error?.text || error.message, { variant: 'error' });
    }

    setGeneratingTextTutorial(false);
  }

  function actionResolver(action) {
    if (action.type === 'dropdown') {
      return (
        <HeaderDropdown
          title={action.title}
          icon={action.icon}
          items={action.items || []}
          label={action.label}
          className={action.className}
        />
      );
    }

    if (action.type === 'button') {
      return (
        <HeaderActionButton
          key={action.title}
          label={action.title}
          cb={action.callback}
          disabled={action.disabled}
          icon={action.icon}
          className={action.className}
        />
      );
    }

    if (action.type === 'text_tutorial') {
      return (
        <Box
          key={action.type}
          className={`h-full flex items-center gap-8 py-10 px-12 text-white ${
            generatingTextTutorial ? 'cursor-wait' : 'cursor-pointer'
          }`}
          onClick={handleTextTutorialClick}
        >
          <Typography className="font-600 text-12">View Guide</Typography>
          <TextTutorialIcon className="w-16 shrink-0" />
        </Box>
      );
    }

    if (action.type === 'video_tutorial') {
      return (
        <Box
          key={action.type}
          className="h-full flex items-center gap-10 py-10 px-12 text-white cursor-pointer"
          onClick={() => {
            // Track watch tutorial click if on managed repeats page
            if (isOnManagedRepeatsPage) {
              trackEvent(
                MANAGED_REPEATS_EVENTS.WATCH_TUTORIAL_CLICKED.action,
                MANAGED_REPEATS_EVENTS.WATCH_TUTORIAL_CLICKED
              );
            }
            setVideoTutorialOpened(true);
          }}
        >
          <Typography className="font-600 text-12">Watch tutorial</Typography>
          <VideoTutorialIcon className="w-16 shrink-0" />
        </Box>
      );
    }

    if (action.type === 'select') {
      return (
        <Select
          className="w-min h-full font-600 text-14 text-dark bg-white rounded-4 border-0 outline-0"
          displayEmpty
          value={action.defaultValue}
          onChange={(e) => action.callback(e.target.value)}
          disabled={action.disabled}
          sx={selectReset}
        >
          {action.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (action.type === 'link') {
      return (
        <Box className="h-full flex items-center">
          <a href={action.url} target="_blank" rel="noopener noreferrer" className="text-white">
            {action.title}
          </a>
        </Box>
      );
    }

    if (action.type === 'label') {
      return (
        <Box
          key={action.title}
          className={`h-full flex items-center gap-8 py-10 px-12 rounded-4 ${
            action.variant === 'success'
              ? 'bg-green-600 text-white'
              : action.variant === 'warning'
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-500 text-white'
          }`}
        >
          <Typography className="font-600 text-12">{action.title}</Typography>
        </Box>
      );
    }

    return null;
  }

  return (
    <HeaderContainer className="flex justify-between p-8 h-full" $background={customBackground}>
      <Box className="h-full flex items-center">
        {organisationSelector && <OrganisationSelector />}
        {pharmacySelector && (
          <PharmacySelector disabled={pharmacySelectorDisabled} hidden={pharmacySelectorHidden} />
        )}
        {headerTitle && (
          <Typography className="text-white font-600 text-16 px-16">{headerTitle}</Typography>
        )}
        {headerTitleDisabled && (
          <Typography className="text-typography-danger text-16">[Disabled]</Typography>
        )}
      </Box>
      <Box className="flex gap-12 h-full items-center">
        {!isPaymentBlocked &&
          headerActions?.length > 0 &&
          headerActions.map((action) => (
            <Box key={`${action.type}_${action.title}`} className="relative h-full">
              {actionResolver(action)}
              {action.pro && <img src={proIcon} alt="pro feature" className="pro-tag h-20" />}
            </Box>
          ))}
      </Box>
      {videoTutorial && (
        <Modal
          open={videoTutorialOpened}
          onClose={() => setVideoTutorialOpened(false)}
          className="flex justify-center items-center"
        >
          <Box className="w-[80%]">
            <iframe
              className="w-full aspect-video"
              title="Video tutorial"
              src={videoTutorial.url}
              allowFullScreen
            />
          </Box>
        </Modal>
      )}
    </HeaderContainer>
  );
}

export default Header;

const DEFAULT_BACKGROUND = 'linear-gradient(102.82deg, #4db181 5.6%, #3f7284 85.37%)';

export const HEADER_BACKGROUNDS = {
  default: DEFAULT_BACKGROUND,
  featured: '#6448F1',
  marketing: 'linear-gradient(102.82deg, #5764F8 5.6%, #89338B 85.37%)',
};

const HeaderContainer = styled(Box)`
  background: ${({ $background }) => HEADER_BACKGROUNDS[$background] || DEFAULT_BACKGROUND};
`;

const selectReset = {
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
};

function HeaderActionButton({ cb, disabled, label, icon, className: extraClassName, ...props }) {
  const hasExternalTextColor = /\btext-(?!14\b)\S+/.test(extraClassName || '');
  const baseClassName = [
    'bg-white',
    'rounded-4',
    'font-600',
    'text-14',
    !hasExternalTextColor && 'text-typography-primary',
    'hover:bg-neutral-200',
    'disabled:opacity-50',
    'transition-opacity',
    'transition-colors',
    'h-full',
    'px-8',
  ].filter(Boolean);

  return (
    <Button onClick={cb} disabled={disabled} className={`${baseClassName.join(' ')} ${extraClassName || ''}`} {...props}>
      {icon && <Box className="mr-10">{icon}</Box>}
      {label}
    </Button>
  );
}
